import type { CoClient } from "./types";

// The onboarding IA: 5 always-visible main steps, each with nested
// sub-steps — ported from zournal-client-onboarding
// src/lib/onboarding-v2/steps.ts. Every consumer of the step list (left
// nav, Continue navigation, progress) goes through visibleMainSteps()/
// visibleSubSteps() rather than the raw catalog, so a step that doesn't
// apply to this client is never shown or landed on.

export const CO_BASE = "/admin/client-onboarding";

export const SHOW_DRY_RUN = false;

export interface CoSubStep {
  slug: string;
  title: string;
}

export interface CoMainStep {
  id: string;
  title: string;
  subSteps: CoSubStep[];
}

interface CoSubStepDef extends CoSubStep {
  appliesTo?: (client: CoClient) => boolean;
}

interface CoMainStepDef {
  id: string;
  title: string;
  subSteps: CoSubStepDef[];
  appliesTo?: (client: CoClient) => boolean;
}

// True when this client needs a books connection set up at all: Books was
// selected directly, or CFO was selected alone and chose "connect".
// Compliance-only clients never want this group — they run on monthly
// uploads instead.
export function wantsBooksConnection(client: CoClient): boolean {
  if (client.services.includes("books")) return true;
  return client.services.includes("cfo") && !client.services.includes("books") && client.cfoSource === "connect";
}

// CFO-only clients who chose to upload a trial balance instead of
// connecting get a dedicated upload sub-step in place of the whole
// "Connect the books" group.
export function wantsCfoTrialBalanceUpload(client: CoClient): boolean {
  const cfoOnly = client.services.includes("cfo") && !client.services.includes("books");
  return cfoOnly && client.cfoSource === "upload";
}

// A live connection means there's real books data to read back and deepen;
// anything else falls back to the single manual read-back step instead.
export function isBooksConnected(client: CoClient): boolean {
  return client.intakeMode === "fetch";
}

const CO_STEP_DEFS: CoMainStepDef[] = [
  {
    id: "client-basics",
    title: "Client basics",
    subSteps: [{ slug: "company-details", title: "Company details" }],
  },
  {
    id: "connect-books",
    title: "Connect the books",
    appliesTo: (client) => wantsBooksConnection(client),
    subSteps: [
      { slug: "books-questions", title: "Connect Your Accounting Software" },
      { slug: "account-start-date", title: "Start date" },
      {
        slug: "package-sizing",
        title: "Select the Correct Zoho Books Subscription Package",
        appliesTo: (client) => client.backend.system === "zoho" && wantsBooksConnection(client),
      },
      {
        slug: "zoho-account",
        title: "Connecting Your Zoho Account",
        appliesTo: (client) => client.backend.system === "zoho",
      },
      {
        slug: "add-zournal",
        title: "Connect Zournal to Zoho Books",
        appliesTo: (client) => client.backend.system === "zoho",
      },
      {
        slug: "confirm-connection",
        title: "Confirm Connection to Zoho",
        appliesTo: (client) => client.backend.system === "zoho",
      },
      {
        slug: "connect-tally",
        title: "Connect Zournal to Tally",
        appliesTo: (client) => client.backend.system === "tally",
      },
    ],
  },
  {
    id: "verify-setup",
    title: "Verify setup",
    subSteps: [
      // The chart of accounts leads because everything after it is read against
      // it: a GST rate, a TDS section and a bank all post somewhere, and that
      // somewhere is a ledger. Same order the reference flow uses.
      { slug: "ledgers", title: "Set Up Your Chart of Accounts", appliesTo: (client) => isBooksConnected(client) },
      {
        slug: "rounding-off",
        title: "Configure rounding off",
        appliesTo: (client) => isBooksConnected(client),
      },
      {
        slug: "cfo-trial-balance",
        title: "Trial balance upload",
        appliesTo: (client) => wantsCfoTrialBalanceUpload(client),
      },
      { slug: "readback-review", title: "Read-back review", appliesTo: (client) => !isBooksConnected(client) },
      { slug: "gst-review", title: "GST Settings", appliesTo: (client) => isBooksConnected(client) },
      { slug: "tds-tcs-review", title: "TDS & TCS Settings", appliesTo: (client) => isBooksConnected(client) },
      // Currency leads banks because an account carries one, and a code has to
      // exist before a bank in that currency can be created.
      { slug: "currency", title: "Currency Settings", appliesTo: (client) => isBooksConnected(client) },
      { slug: "banks-review", title: "Banks & Credit Cards", appliesTo: (client) => isBooksConnected(client) },
      { slug: "customers-vendors", title: "Set Up Customers & Vendors", appliesTo: (client) => isBooksConnected(client) },
      { slug: "opening-balances", title: "Set Up Opening Balances" },
      { slug: "prepare-zoho", title: "Setting Up Zoho", appliesTo: (client) => isBooksConnected(client) },
    ],
  },
  {
    id: "team-rules",
    title: "Team & rules",
    subSteps: [
      // Approval rules used to sit between these two. It is gone: the review
      // package, who reviews, and the function catalog all moved into team
      // assignment's module tabs, where a policy belongs to the tab that owns
      // the function rather than to the client as a whole.
      { slug: "team-assignment", title: "Assign Your Team" },
      { slug: "escalations-access", title: "Set Up Client Contacts" },
    ],
  },
  {
    id: "go-live",
    title: "Go live",
    subSteps: [
      { slug: "month-close-checklist", title: "Month-close checklist" },
      {
        slug: "catch-up-training",
        title: "Catch-up & training",
        // Only books with a past can be caught up on or learned from: an
        // existing Zoho organisation, or the catch-up route (existing books,
        // just behind). New organisations, brand-new companies and clients
        // with no books connection skip the step — Books questions records
        // its "start fresh" answer for them the moment the type is chosen.
        appliesTo: (client) =>
          client.bookkeepingType === "existing-zoho-current" || client.bookkeepingType === "catch-up",
      },
      { slug: "dry-run", title: "Dry run", appliesTo: () => SHOW_DRY_RUN },
      { slug: "start-date", title: "Go live" },
    ],
  },
];

export const ALL_CO_SUBSTEPS: CoSubStep[] = CO_STEP_DEFS.reduce<CoSubStep[]>(
  (all, step) => all.concat(step.subSteps),
  []
);

export const FIRST_CO_SLUG = ALL_CO_SUBSTEPS[0].slug;

// The client-filtered step list every nav/progress consumer should read.
export function visibleMainSteps(client: CoClient): CoMainStep[] {
  return CO_STEP_DEFS.filter((step) => !step.appliesTo || step.appliesTo(client)).map((step) => ({
    id: step.id,
    title: step.title,
    subSteps: step.subSteps
      .filter((sub) => !sub.appliesTo || sub.appliesTo(client))
      .map((sub) =>
        sub.slug === "prepare-zoho" && client.backend.system === "tally"
          ? { ...sub, title: "Setting up Tally" }
          : sub
      ),
  }));
}

export function visibleSubSteps(client: CoClient): CoSubStep[] {
  return visibleMainSteps(client).reduce<CoSubStep[]>((all, step) => all.concat(step.subSteps), []);
}

export function findMainStepForSlug(client: CoClient, slug: string): CoMainStep | undefined {
  return visibleMainSteps(client).find((step) => step.subSteps.some((sub) => sub.slug === slug));
}

export function nextSlugAfter(client: CoClient, slug: string): string | undefined {
  const subs = visibleSubSteps(client);
  const index = subs.findIndex((sub) => sub.slug === slug);
  if (index === -1) return undefined;
  return subs[index + 1] ? subs[index + 1].slug : undefined;
}

export function isValidSlug(slug: string): boolean {
  return ALL_CO_SUBSTEPS.some((sub) => sub.slug === slug);
}

/**
 * The step the flow is waiting on: the first visible sub-step not yet done.
 * Falls back to the last sub-step once everything is done.
 */
export function frontierSlug(client: CoClient, progress: { doneSlugs: string[] }): string {
  const subs = visibleSubSteps(client);
  const next = subs.find((sub) => !progress.doneSlugs.includes(sub.slug));
  return next ? next.slug : subs[subs.length - 1].slug;
}

/**
 * Whether a sub-step may be opened: anything already done, the step being
 * looked at, or the frontier. Later steps stay locked until Continue walks
 * the flow forward, so nothing downstream runs against half-entered data.
 */
export function isSlugReachable(
  client: CoClient,
  progress: { currentSlug: string; doneSlugs: string[] },
  slug: string
): boolean {
  return (
    progress.doneSlugs.includes(slug) ||
    slug === progress.currentSlug ||
    slug === frontierSlug(client, progress)
  );
}

export type CoStepStatus = "done" | "in-progress" | "not-started";

export function statusForMainStep(step: CoMainStep, progress: { currentSlug: string; doneSlugs: string[] }): CoStepStatus {
  const slugs = step.subSteps.map((sub) => sub.slug);
  if (slugs.length > 0 && slugs.every((slug) => progress.doneSlugs.includes(slug))) return "done";
  if (slugs.some((slug) => progress.doneSlugs.includes(slug) || slug === progress.currentSlug)) return "in-progress";
  return "not-started";
}
